import { Extension } from '@tiptap/core';

const toPxNumber = (value: string): number | null => {
  const trimmed = (value || '').trim();
  const match = trimmed.match(/^(\d+(?:\.\d+)?)px$/i);
  if (!match) {
    return null;
  }
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeFontSizePx = (value: string): string | null => {
  const trimmed = (value || '').trim();
  const directPx = toPxNumber(trimmed);
  if (directPx) {
    return `${directPx}px`;
  }

  const match = trimmed.match(/(\d+(?:\.\d+)?)px/i);
  if (!match) {
    return null;
  }

  const parsed = Number(match[1]);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return `${parsed}px`;
};

const toZoomAwareFontSize = (pxFontSize: string): string => {
  return `calc(${pxFontSize} * var(--fontScaleRatio, 1) * var(--editorDeviceScale, 1))`;
};

const computeLineHeight = (fontSizePx: number): string => {
  // Proportional: line height = fontSizePx * factor, clamped to [1.08, 1.5]
  // 12px => 1.2, 14px => 1.25, 16px => 1.3, 20px => 1.38, 24px => 1.45
  const factor = 0.1;
  const min = 1.08;
  const max = 1.5;
  const lh = fontSizePx * factor;
  return String(Math.max(min, Math.min(max, lh)));
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blockTypography: {
      setBlockFontSize: (fontSize: string) => ReturnType;
      unsetBlockFontSize: () => ReturnType;
    };
  }
}

export const BlockTypography = Extension.create({
  name: 'blockTypography',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'blockquote', 'listItem'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => {
              const value = (element as HTMLElement).style.fontSize || '';
              return normalizeFontSizePx(value) || null;
            },
            renderHTML: (attributes) => {
              const fontSize = String(attributes['fontSize'] || '').trim();
              const lineHeight = String(attributes['lineHeight'] || '').trim();
              const styles: string[] = [];

              if (fontSize) {
                styles.push(`font-size: ${toZoomAwareFontSize(fontSize)}`);
              }
              if (lineHeight) {
                styles.push(`line-height: ${lineHeight}`);
              }

              return styles.length > 0 ? { style: styles.join('; ') } : {};
            },
          },
          lineHeight: {
            default: null,
            parseHTML: (element) => {
              const value = (element as HTMLElement).style.lineHeight || '';
              return value.trim() || null;
            },
            renderHTML: () => ({}),
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setBlockFontSize:
        (fontSize: string) =>
        ({ state, dispatch }) => {
          const normalized = String(fontSize || '').trim();
          const px = toPxNumber(normalized);
          if (!px) {
            return false;
          }

          const nextLineHeight = computeLineHeight(px);
          const { from, to } = state.selection;
          const tr = state.tr;
          let changed = false;

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (!['paragraph', 'heading', 'blockquote', 'listItem'].includes(node.type.name)) {
              return true;
            }

            const nextAttrs = {
              ...node.attrs,
              fontSize: normalized,
              lineHeight: nextLineHeight,
            };

            if (node.attrs['fontSize'] === nextAttrs.fontSize && node.attrs['lineHeight'] === nextAttrs.lineHeight) {
              return true;
            }

            tr.setNodeMarkup(pos, undefined, nextAttrs, node.marks);
            changed = true;
            return true;
          });

          if (!changed) {
            return false;
          }

          if (dispatch) {
            dispatch(tr);
          }

          return true;
        },

      unsetBlockFontSize:
        () =>
        ({ state, dispatch }) => {
          const { from, to } = state.selection;
          const tr = state.tr;
          let changed = false;

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (!['paragraph', 'heading', 'blockquote', 'listItem'].includes(node.type.name)) {
              return true;
            }

            if (!node.attrs['fontSize'] && !node.attrs['lineHeight']) {
              return true;
            }

            const nextAttrs = {
              ...node.attrs,
              fontSize: null,
              lineHeight: null,
            };

            tr.setNodeMarkup(pos, undefined, nextAttrs, node.marks);
            changed = true;
            return true;
          });

          if (!changed) {
            return false;
          }

          if (dispatch) {
            dispatch(tr);
          }

          return true;
        },
    };
  },
});
